import React, { useState } from 'react';
import { CheckCircle2, Loader2, Download, Play, Pause, Film, Layers, Clock, RotateCcw } from 'lucide-react';
import ProgressBar from '../../components/ui/ProgressBar';
import { getVideoExpiryInfo } from '../../utils/videoExpiry';

export function StudioRightPanel({
  project = {},
  catalogs = {},
  currentJob = null,
  onStartRender,
  isStartingRender = false,
  audioUrl = null,
  isPlayingAudio = false,
  onToggleAudio = () => { },
  onOpenCreditModal
}) {
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'scenes'
  const [hasVideoError, setHasVideoError] = useState(false);

  const subjectA = project.subjectA || {};
  const subjectB = project.subjectB || {};
  const script = project.script || {};

  const voices = catalogs.voices || [];
  const selectedVoice = voices.find(v => v.id === project.voiceId) || { name: 'Cô gái hoạt ngôn' };
  const backgrounds = catalogs.backgrounds || [];
  const selectedBg = backgrounds.find(b => b.id === project.backgroundId) || { theme: 'sketch', bgColor: '#FAF8F5' };

  const isJobRunning = currentJob && (currentJob.status === 'QUEUED' || currentJob.status === 'PROCESSING');
  const isJobCompleted = currentJob && currentJob.status === 'COMPLETED';

  // Hạn lưu trữ 3 ngày của video đã render (dùng chung với JobProgressCard)
  const expiryInfo = getVideoExpiryInfo({
    ...(currentJob || {}),
    ...(currentJob?.outputAsset || {}),
    completedAt: currentJob?.completedAt || currentJob?.outputAsset?.createdAt
  });
  const isVideoExpired = expiryInfo.isExpired || hasVideoError;

  // Check inputs
  const hasImages = Boolean(subjectA.imageUrl && subjectB.imageUrl);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        height: '100%'
      }}
    >
      {/* Header & Tab Selector */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--primary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              BƯỚC 3: REVIEW & XUẤT BẢN
            </span>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.2, marginTop: '2px' }}>
              Trung Tâm Review Video
            </h3>
          </div>
        </div>

        {/* View Mode Tabs (2 Clean Tabs: Mô Phỏng 9:16 & Kịch Bản Cảnh) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', background: 'var(--bg-subtle)', padding: '4px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <button
            type="button"
            onClick={() => setActiveTab('preview')}
            style={{
              padding: '7px 10px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              background: activeTab === 'preview' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'preview' ? 'var(--primary)' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: activeTab === 'preview' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all var(--transition-fast)'
            }}
          >
            <Film size={14} />
            <span>Mô Phỏng 9:16</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('scenes')}
            style={{
              padding: '7px 10px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              background: activeTab === 'scenes' ? '#FFFFFF' : 'transparent',
              color: activeTab === 'scenes' ? 'var(--primary)' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: activeTab === 'scenes' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all var(--transition-fast)'
            }}
          >
            <Layers size={14} />
            <span>Kịch Bản Cảnh</span>
          </button>
        </div>
      </div>

      {/* TAB 1: 9:16 ENLARGED PROMINENT SMARTPHONE PREVIEW */}
      {activeTab === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' }}>
          {/* Smartphone Frame Container */}
          <div
            style={{
              width: '100%',
              maxWidth: '330px',
              borderRadius: '32px',
              background: '#0F172A',
              boxShadow: '0 20px 40px -10px rgba(0,0,0,0.35), 0 0 0 10px #1E293B, 0 0 0 12px #334155',
              padding: '10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Camera Pill Notch */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', margin: '2px auto 8px' }}>
              <div style={{ width: '40px', height: '4px', background: '#334155', borderRadius: '3px' }} />
              <div style={{ width: '6px', height: '6px', background: '#1E293B', borderRadius: '50%', border: '1px solid #475569' }} />
            </div>

            {/* Viewport Screen */}
            <div
              style={{
                flex: '0 0 auto',
                width: '100%',
                aspectRatio: '9 / 16',
                borderRadius: '22px',
                overflow: 'hidden',
                backgroundColor: selectedBg.bgColor || '#FAF8F5',
                backgroundImage: selectedBg.imageUrl || selectedBg.previewUrl
                  ? `url(${selectedBg.imageUrl || selectedBg.previewUrl})`
                  : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                flexDirection: 'column',
                padding: '14px 12px 0 12px',
                position: 'relative',
                boxShadow: 'inset 0 0 12px rgba(0,0,0,0.04)'
              }}
            >
              {/* 1. TOP SECTION: HEADER & DUAL COMPARISON CARDS (Prominent & Clear) */}
              <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '6px', zIndex: 10 }}>
                {/* Header inside Phone */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                  <span style={{ fontSize: '9px', fontWeight: 700, background: 'rgba(0,0,0,0.08)', color: '#1C1917', padding: '2px 7px', borderRadius: '6px' }}>
                    1080x1920 (9:16)
                  </span>
                </div>

                {/* Dual Comparison Cards (Side-by-side, Prominent 1:1 image boxes) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {/* Product A */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <span
                      style={{
                        fontSize: '11.5px',
                        fontWeight: 800,
                        color: '#DC2626',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                        width: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {subjectA.name || 'PHƯƠNG ÁN A'}
                    </span>
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        maxHeight: '100px',
                        borderRadius: '12px',
                        background: subjectA.imageUrl ? '#FFFFFF' : 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)',
                        border: '2px solid rgba(220, 38, 38, 0.25)',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(220, 38, 38, 0.12)'
                      }}
                    >
                      {subjectA.imageUrl ? (
                        <img src={subjectA.imageUrl} alt="A" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '15px', fontWeight: 800, color: '#FFFFFF' }}>Ảnh A</span>
                      )}
                    </div>
                  </div>

                  {/* Product B */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <span
                      style={{
                        fontSize: '11.5px',
                        fontWeight: 800,
                        color: '#2563EB',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                        width: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {subjectB.name || 'PHƯƠNG ÁN B'}
                    </span>
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        maxHeight: '100px',
                        borderRadius: '12px',
                        background: subjectB.imageUrl ? '#FFFFFF' : 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                        border: '2px solid rgba(37, 99, 235, 0.25)',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(37, 99, 235, 0.12)'
                      }}
                    >
                      {subjectB.imageUrl ? (
                        <img src={subjectB.imageUrl} alt="B" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '15px', fontWeight: 800, color: '#FFFFFF' }}>Ảnh B</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. MIDDLE SECTION: CENTER VS SUBTITLE CAPTION BANNER */}
              <div
                style={{
                  flex: '0 0 auto',
                  margin: '6px 0 2px 0',
                  padding: '6px 10px',
                  background: '#FFFFFF',
                  borderRadius: '10px',
                  border: '1.5px solid #F59E0B',
                  boxShadow: '0 3px 10px rgba(245, 158, 11, 0.15)',
                  textAlign: 'center',
                  zIndex: 15
                }}
              >
                <div style={{ display: 'inline-block', background: '#FEF3C7', color: '#B45309', fontSize: '8.5px', fontWeight: 800, padding: '1px 6px', borderRadius: '4px' }}>
                  VS • LỜI BÌNH
                </div>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#1C1917', marginTop: '2px', lineHeight: 1.3, maxHeight: '34px', overflow: 'hidden' }}>
                  {script.hook?.narration || 'Bạn đang phân vân giữa 2 lựa chọn? Đây là video so sánh chuẩn xác nhất!'}
                </p>
              </div>

              {/* 3. BOTTOM SECTION: MASCOT / HOST CHARACTER (Balanced & Anchored to Bottom) */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  zIndex: 5
                }}
              >
                {project.avatarUrl ? (
                  <img
                    src={project.avatarUrl}
                    alt="Mascot Avatar"
                    style={{
                      width: 'auto',
                      height: '100%',
                      maxHeight: '230px',
                      objectFit: 'contain',
                      objectPosition: 'bottom center',
                      filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
                    }}
                  />
                ) : (
                  /* Vector Host / Mascot Pharmacist */
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', width: '100%', height: '100%', justifyContent: 'flex-end' }}>
                    {/* Sparkle above pointing hand */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '22%',
                        color: '#F59E0B',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        animation: 'bounce 1.5s infinite'
                      }}
                    >
                      ✦
                    </div>

                    {/* Head & Glasses */}
                    <div
                      style={{
                        width: '54px',
                        height: '54px',
                        borderRadius: '50%',
                        background: '#FFFFFF',
                        border: '3px solid #1E293B',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {/* Hair tuft */}
                      <div style={{ position: 'absolute', top: '-6px', width: '22px', height: '8px', background: '#1E293B', borderRadius: '4px' }} />
                      {/* Round Glasses */}
                      <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                        <div style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid #1E293B' }} />
                        <div style={{ width: '4px', height: '2px', background: '#1E293B' }} />
                        <div style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid #1E293B' }} />
                      </div>
                      {/* Smile */}
                      <div style={{ position: 'absolute', bottom: '6px', width: '14px', height: '3px', background: '#1E293B', borderRadius: '2px' }} />
                    </div>

                    {/* Bowtie */}
                    <div style={{ width: '14px', height: '8px', background: '#059669', clipPath: 'polygon(0% 0%, 100% 0%, 50% 50%, 100% 100%, 0% 100%, 50% 50%)', margin: '-2px 0' }} />

                    {/* Body / Polo Shirt */}
                    <div
                      style={{
                        width: '50px',
                        height: '54px',
                        background: '#059669',
                        borderRadius: '8px 8px 0 0',
                        border: '2.5px solid #1E293B',
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Pointing Right Arm */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '-20px',
                          width: '26px',
                          height: '42px',
                          borderTop: '3.5px solid #1E293B',
                          borderRight: '3.5px solid #1E293B',
                          borderRadius: '0 12px 0 0',
                          transform: 'rotate(-25deg)'
                        }}
                      />
                      {/* Left Hand */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '10px',
                          left: '-14px',
                          width: '16px',
                          height: '24px',
                          borderTop: '3.5px solid #1E293B',
                          borderLeft: '3.5px solid #1E293B',
                          borderRadius: '10px 0 0 0'
                        }}
                      />
                    </div>

                    {/* Legs */}
                    <div style={{ display: 'flex', gap: '14px' }}>
                      <div style={{ width: '4px', height: '34px', background: '#1E293B' }} />
                      <div style={{ width: '4px', height: '34px', background: '#1E293B' }} />
                    </div>

                    {/* Shoes */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '-2px' }}>
                      <div style={{ width: '16px', height: '7px', background: '#1E293B', borderRadius: '4px' }} />
                      <div style={{ width: '16px', height: '7px', background: '#1E293B', borderRadius: '4px' }} />
                    </div>

                    {/* Bottom Branding Tag */}
                    <div style={{ background: '#059669', color: '#FFFFFF', fontSize: '8px', fontWeight: 800, padding: '2px 8px', borderRadius: '4px 4px 0 0', marginTop: '1px', letterSpacing: '0.04em' }}>
                      DƯỢC SĨ 5.0
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Audio TTS Preview Button */}
          <button
            type="button"
            onClick={onToggleAudio}
            style={{
              width: '100%',
              maxWidth: '330px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: isPlayingAudio ? '#059669' : '#FFFFFF',
              border: `1.5px solid ${isPlayingAudio ? '#059669' : 'var(--border-color)'}`,
              borderRadius: 'var(--radius-md)',
              color: isPlayingAudio ? '#FFFFFF' : 'var(--text-main)',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
              transition: 'all var(--transition-fast)'
            }}
          >
            {isPlayingAudio ? <Pause size={16} /> : <Play size={16} color="var(--primary)" />}
            <span>{isPlayingAudio ? 'Tạm Dừng Giọng Đọc' : `Nghe Thử Giọng Đọc (${selectedVoice.name})`}</span>
          </button>
        </div>
      )}

      {/* TAB 2: SCENE BREAKDOWN */}
      {activeTab === 'scenes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '480px', overflowY: 'auto' }}>
          <div style={{ padding: '10px 12px', background: '#FFF7ED', border: '1px solid rgba(234, 88, 12, 0.2)', borderRadius: '8px', fontSize: '12px', color: '#C2410C' }}>
            Chi tiết kịch bản và thứ tự hiển thị của từng cảnh trong video.
          </div>

          {/* Hook */}
          <div style={{ padding: '12px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--primary)' }}>CẢNH 1: MỞ ĐẦU (HOOK)</span>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>~4s • Pose: smile</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-main)', lineHeight: 1.4 }}>
              {script.hook?.narration || 'Bạn đang phân vân giữa 2 lựa chọn?'}
            </p>
          </div>

          {/* Sections */}
          {(script.sections || []).map((sec, idx) => (
            <div key={idx} style={{ padding: '12px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 800, color: idx === 0 ? '#DC2626' : '#2563EB' }}>
                  CẢNH {idx + 2}: {sec.title || (idx === 0 ? 'PHƯƠNG ÁN A' : 'PHƯƠNG ÁN B')}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                  ~{sec.durationSec || 8}s • {idx === 0 ? 'point_left' : 'point_right'}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-main)', lineHeight: 1.4 }}>
                {sec.narration}
              </p>
            </div>
          ))}

          {/* Conclusion */}
          <div style={{ padding: '12px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#059669' }}>CẢNH CUỐI: TỔNG KẾT & CTA</span>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>~6s • Pose: open_hand</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-main)', lineHeight: 1.4 }}>
              {script.conclusion?.narration || script.cta?.narration || 'Chốt lại: chọn đúng theo nhu cầu!'}
            </p>
          </div>
        </div>
      )}

      {/* Render Checklist Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px', background: 'var(--bg-subtle)', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>Tiến độ render</span>
            <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '10px' }}>
              Giọng đọc: {selectedVoice.name}
            </span>
          </div>
          <span style={{ fontWeight: 800, color: isJobRunning ? '#EA580C' : isJobCompleted ? '#10B981' : 'var(--text-dim)' }}>
            {currentJob?.progress || 0}%
          </span>
        </div>

        {isJobRunning && (
          <ProgressBar value={currentJob?.progress || 0} color="#EA580C" height="6px" />
        )}

        <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Dữ liệu ảnh A/B</span>
          <span style={{ fontWeight: 600, color: hasImages ? '#10B981' : '#EA580C' }}>
            {hasImages ? 'Đầy đủ ảnh A & B' : 'Cần tải ảnh A & B'}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Chi phí lượt xuất</span>
          <span style={{ fontWeight: 700, color: 'var(--primary)' }}>
            10 credit / video
          </span>
        </div>
      </div>

      {/* Render Action Button */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: 'auto' }}>
        <button
          type="button"
          disabled={isStartingRender || isJobRunning}
          onClick={onStartRender}
          style={{
            padding: '14px',
            background: 'var(--primary-gradient)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: '#FFFFFF',
            fontSize: '15px',
            fontWeight: 800,
            cursor: isJobRunning ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 14px rgba(234, 88, 12, 0.35)',
            opacity: isJobRunning ? 0.7 : 1,
            transition: 'all var(--transition-fast)'
          }}
        >
          {isJobRunning ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Đang Render MP4 ({currentJob?.progress || 0}%)...</span>
            </>
          ) : (
            <span>🚀 Bắt Đầu Render Video Full HD</span>
          )}
        </button>

        <button
          type="button"
          onClick={onOpenCreditModal}
          style={{
            padding: '8px',
            background: 'transparent',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-muted)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Xem bảng giá & định mức credit
        </button>
      </div>

      {/* Output Video Card if completed */}
      {isJobCompleted && currentJob?.outputAsset && (
        isVideoExpired ? (
          <div
            style={{
              background: '#FFFBEB',
              border: '1.5px solid #F59E0B',
              borderRadius: 'var(--radius-md)',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              textAlign: 'center',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                background: 'rgba(245, 158, 11, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Clock size={20} color="#D97706" />
            </div>

            <div>
              <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#B45309', marginBottom: '4px' }}>
                Video đã hết hạn, vui lòng tạo lại
              </h4>
              <p style={{ fontSize: '11px', color: '#78350F', lineHeight: 1.4, margin: 0 }}>
                Video được lưu tối đa 3 ngày và máy chủ đã tự động xoá file. Kịch bản của bạn vẫn được giữ nguyên.
              </p>
            </div>

            <button
              type="button"
              onClick={onStartRender}
              disabled={isStartingRender}
              style={{
                width: '100%',
                padding: '9px',
                background: 'var(--primary-gradient)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <RotateCcw size={14} />
              <span>Tạo lại video ngay</span>
            </button>
          </div>
        ) : (
          <div
            style={{
              background: '#FFFFFF',
              border: '1.5px solid #10B981',
              borderRadius: 'var(--radius-md)',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={16} color="#10B981" />
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#065F46' }}>
                  Video đã render thành công!
                </span>
              </div>
              <span style={{ fontSize: '10px', color: '#059669', fontWeight: 700 }}>
                Full HD MP4
              </span>
            </div>

            {/* Banner thời hạn lưu trữ 3 ngày */}
            <div
              style={{
                padding: '6px 10px',
                background: expiryInfo.isUrgent ? '#FEE2E2' : '#FEF3C7',
                border: `1px solid ${expiryInfo.isUrgent ? '#FCA5A5' : '#FDE68A'}`,
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: expiryInfo.isUrgent ? '#991B1B' : '#92400E'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} />
                <strong>Hạn 3 ngày:</strong> {expiryInfo.remainingText}
              </span>
              <span>{expiryInfo.formattedExpiry}</span>
            </div>

            <video
              src={currentJob.outputAsset.fileUrl}
              controls
              autoPlay
              playsInline
              onError={() => setHasVideoError(true)}
              style={{ width: '100%', borderRadius: '6px', maxHeight: '180px', background: '#000000' }}
            />

            <a
              href={currentJob.outputAsset.fileUrl}
              download
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#10B981',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 700,
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Download size={14} />
                <span>Tải video MP4 (Hạn 3 ngày)</span>
              </button>
            </a>
          </div>
        )
      )}
    </div>
  );
}

export default StudioRightPanel;
